import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ShieldAlert, Eye, EyeOff, LogOut, Loader2, AlertCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { describeAuthError, type FriendlyAuthError } from "@/lib/authErrors";
import { showLeakedPasswordToast } from "@/lib/authErrorToast";
import { STRONG_PASSWORD_EXAMPLES, STRONG_PASSWORD_TIP } from "@/lib/passwordGuidance";
import { sanitizeRedirectPath } from "@/lib/safeRedirect";

const PasswordResetRequired = () => {
  const { user, loading, mustResetPassword, clearMustResetPassword, signOut } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [submitError, setSubmitError] = useState<FriendlyAuthError | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const fromState = (location.state as { from?: unknown } | null)?.from;
  const redirectTarget = sanitizeRedirectPath(fromState, "/dashboard");

  if (!loading && !user) {
    navigate("/login", { replace: true });
    return null;
  }

  // If the flag was already cleared, don't strand the user on this page.
  if (!loading && user && !mustResetPassword) {
    navigate(redirectTarget, { replace: true });
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (password.length < 8) {
      toast({
        title: "Password too short",
        description: "Please use at least 8 characters.",
        variant: "destructive",
      });
      return;
    }
    if (password !== confirm) {
      toast({
        title: "Passwords don't match",
        description: "Please make sure both passwords match.",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    let result: Awaited<ReturnType<typeof supabase.auth.updateUser>>;
    try {
      result = await supabase.auth.updateUser({ password });
    } catch (err) {
      setIsSaving(false);
      const friendly = describeAuthError(err, { context: "update" });
      setSubmitError(friendly);
      toast({ title: friendly.title, description: friendly.description, variant: "destructive" });
      return;
    }
    setIsSaving(false);
    const { error } = result;

    if (error) {
      const friendly = describeAuthError(error, { context: "update" });
      setSubmitError(friendly);
      if (friendly.isLeakedPassword) {
        showLeakedPasswordToast(toast, friendly, { navigate });
      } else {
        toast({ title: friendly.title, description: friendly.description, variant: "destructive" });
      }
      return;
    }

    clearMustResetPassword();
    toast({
      title: "Password updated",
      description: "Thanks for keeping your account secure.",
    });
    navigate(redirectTarget, { replace: true });
  };

  const handleFieldChange =
    (setter: (v: string) => void) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (submitError) setSubmitError(null);
      setter(e.target.value);
    };


  const handleSignOut = async () => {
    await signOut();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-12">
      <Helmet>
        <title>Password Reset Required | Elara Health</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className="w-full max-w-md space-y-6">
        <div className="text-center space-y-2">
          <div className="mx-auto h-12 w-12 rounded-full bg-destructive/10 text-destructive flex items-center justify-center">
            <ShieldAlert size={24} aria-hidden="true" />
          </div>
          <h1 className="font-display text-3xl text-foreground">Password reset required</h1>
          <p className="text-muted-foreground">
            To keep your account safe, please set a new password before continuing.
          </p>
        </div>

        <Alert variant="destructive">
          <AlertTitle>Your current password was found in a public data breach</AlertTitle>
          <AlertDescription className="space-y-3">
            <p>
              The password you signed in with has appeared in one or more known data breaches, which
              means attackers may already be trying it against accounts across the internet. We're
              requiring you to change it now before you can access the rest of Elara Health.
            </p>
            <div>
              <p className="font-medium">Examples of strong passwords:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                {STRONG_PASSWORD_EXAMPLES.map((example) => (
                  <li key={example}>{example}</li>
                ))}
              </ul>
            </div>
            <p className="text-sm">{STRONG_PASSWORD_TIP}</p>
          </AlertDescription>
        </Alert>

        <Card>
          <CardHeader>
            <CardTitle>Set a new password</CardTitle>
            <CardDescription>Signed in as {user?.email}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4" aria-busy={isSaving}>
              {submitError ? (
                <Alert
                  variant="destructive"
                  role="alert"
                  aria-live="assertive"
                  data-testid="reset-error"
                >
                  <AlertCircle size={16} aria-hidden="true" />
                  <AlertTitle>{submitError.title}</AlertTitle>
                  <AlertDescription className="space-y-3">
                    <p>{submitError.description}</p>
                    <div className="flex flex-wrap gap-2 pt-1">
                      <Button
                        type="submit"
                        size="sm"
                        variant="secondary"
                        disabled={isSaving}
                      >
                        Try again
                      </Button>
                      {submitError.resetCta ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(submitError.resetCta!.path)}
                        >
                          {submitError.resetCta.label}
                        </Button>
                      ) : null}
                    </div>
                  </AlertDescription>
                </Alert>
              ) : null}

              <div className="space-y-2">
                <Label htmlFor="new-password">New password</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    value={password}
                    onChange={handleFieldChange(setPassword)}
                    required
                    minLength={8}
                    disabled={isSaving}
                    aria-invalid={submitError ? true : undefined}
                    aria-describedby={submitError ? "reset-error" : undefined}
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground disabled:opacity-50"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    aria-pressed={showPassword}
                    disabled={isSaving}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm new password</Label>
                <Input
                  id="confirm-password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  value={confirm}
                  onChange={handleFieldChange(setConfirm)}
                  required
                  minLength={8}
                  disabled={isSaving}
                  aria-invalid={submitError ? true : undefined}
                />
              </div>
              <Button type="submit" className="w-full gap-2" disabled={isSaving}>
                {isSaving ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : null}
                {isSaving ? "Updating your password…" : "Update password and continue"}
              </Button>
              <p
                className="text-xs text-muted-foreground text-center"
                aria-live="polite"
                role="status"
              >
                {isSaving ? "Contacting the server — this usually takes a moment." : "\u00A0"}
              </p>
            </form>
          </CardContent>
        </Card>

        <Button
          variant="ghost"
          className="w-full gap-2"
          onClick={handleSignOut}
          disabled={isSaving}
        >
          <LogOut size={14} /> Sign out instead
        </Button>

      </div>
    </div>
  );
};

export default PasswordResetRequired;
